import SHA256 from 'crypto-js/sha256';
import { 
  Competition, 
  Statistics, 
  Session, 
  Team, 
  Gymnast, 
  Judge, 
  Coach,
  AgeGroup,
  Event,
  Rotation
} from '../types/competition';

interface ImportMetadata {
  exportDate: string;
  checksum: string;
  version: string;
}

interface ImportData {
  competition: Competition;
  statistics: Statistics;
  sessions: Session[];
  teams: Team[];
  gymnasts: Gymnast[];
  levels: string[];
  judges: Judge[];
  coaches: Coach[];
  ageGroups: AgeGroup[];
  events: Event[];
  rotations: Rotation[];
  metadata: ImportMetadata;
}

class JSONParser {
  private content: any;

  constructor(jsonString: string) {
    try {
      this.content = JSON.parse(jsonString);
    } catch (error) {
      throw new Error('Invalid JSON format');
    }
  }

  static async fromFile(file: File): Promise<JSONParser> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          resolve(new JSONParser(event.target.result as string));
        } else {
          reject(new Error('Failed to read file'));
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  }

  private validateMetadata(): void {
    const { metadata } = this.content;

    if (!metadata) {
      throw new Error('Missing metadata section');
    }

    if (!metadata.exportDate || !metadata.checksum || !metadata.version) {
      throw new Error('Invalid metadata format');
    }

    // Verify the checksum
    const dataForHash = JSON.stringify({
      competition: this.content.competition,
      sessions: this.content.sessions,
      gymnasts: this.content.gymnasts,
      levels: this.content.levels,
      teams: this.content.teams,
      judges: this.content.judges,
      coaches: this.content.coaches,
      ageGroups: this.content.ageGroups,
      events: this.content.events,
      rotations: this.content.rotations,
      statistics: this.content.statistics,
    }, Object.keys({}).sort());

    const calculatedChecksum = SHA256(dataForHash).toString();

    if (calculatedChecksum !== metadata.checksum) {
      throw new Error('Checksum verification failed');
    }
  }

  private validateRequiredSections(): void {
    const requiredSections = ['competition', 'statistics', 'sessions', 'teams', 'gymnasts', 'levels', 'rotations', 'ageGroups', 'events'];

    for (const section of requiredSections) {
      if (!this.content[section]) {
        throw new Error(`Missing required section: ${section}`);
      }
    }

    // Validate gymnast data structure
    this.content.gymnasts.forEach((gymnast: any, index: number) => {
      const requiredGymnastFields = ['id', 'firstName', 'lastName', 'teamId', 'level'];
      requiredGymnastFields.forEach(field => {
        if (!gymnast[field]) {
          throw new Error(`Missing required field "${field}" in gymnast at index ${index}`);
        }
      });

      // Type validation
      if (typeof gymnast.id !== 'string') throw new Error(`Invalid id type for gymnast at index ${index}`);
      if (typeof gymnast.firstName !== 'string') throw new Error(`Invalid firstName type for gymnast at index ${index}`);
      if (typeof gymnast.lastName !== 'string') throw new Error(`Invalid lastName type for gymnast at index ${index}`);
      if (typeof gymnast.teamId !== 'string') throw new Error(`Invalid teamId type for gymnast at index ${index}`);
      if (typeof gymnast.level !== 'string') throw new Error(`Invalid level type for gymnast at index ${index}`);
      
      // Squad and age group validation (debug logging removed)
      
      if (gymnast.ageGroup && typeof gymnast.ageGroup !== 'string') {
        throw new Error(`Invalid ageGroup type for gymnast at index ${index}`);
      }
      if (gymnast.squad && typeof gymnast.squad !== 'string') {
        throw new Error(`Invalid squad type for gymnast at index ${index}`);
      }
      if (gymnast.programAgeGroupAge && typeof gymnast.programAgeGroupAge !== 'string') {
        throw new Error(`Invalid programAgeGroupAge type for gymnast at index ${index}`);
      }
      
      // Optional field type validation
      if (gymnast.gender && !['M', 'F', 'O'].includes(gymnast.gender)) {
        throw new Error(`Invalid gender value for gymnast at index ${index}`);
      }
      if (gymnast.status && !['active', 'inactive'].includes(gymnast.status)) {
        throw new Error(`Invalid status value for gymnast at index ${index}`);
      }
    });
  }

  // Check if competition already exists
  competitionExists(existingCompetitions: any[]): boolean {
    const currentCompetition = this.content?.competition;
    if (!currentCompetition || !Array.isArray(existingCompetitions)) {
      return false;
    }
    return existingCompetitions.some(comp =>
      comp?.competition?.name === currentCompetition.name &&
      comp?.competition?.startDate === currentCompetition.startDate &&
      comp?.competition?.endDate === currentCompetition.endDate
    );
  }

  parse(): ImportData {
    this.validateRequiredSections();
    this.validateMetadata();

    // Debug logging removed

    const mappedGymnasts = this.content.gymnasts.map((g: any) => {
      // Try all possible case variations
      const mapped = {
        ...g,
        ageGroup: g.ageGroup || g.AgeGroup || g.AGEGROUP || undefined,
        squad: g.squad || g.Squad || g.SQUAD || undefined,
        programAgeGroupAge: g.programAgeGroupAge || g.ProgramAgeGroupAge || g.PROGRAMAGEGROUPAGE || undefined
      };

      return mapped;
    });

    return {
      competition: this.content.competition,
      sessions: this.content.sessions,
      gymnasts: mappedGymnasts,
      levels: this.content.levels,
      teams: this.content.teams,
      judges: this.content.judges,
      coaches: this.content.coaches,
      ageGroups: this.content.ageGroups,
      events: this.content.events,
      rotations: this.content.rotations,
      statistics: this.content.statistics,
      metadata: this.content.metadata,
    };
  }
}

export default JSONParser;
